<template>
  <div class="w-100" v-if="showTitle">
    <h1 class="ps-1 text-black py-0 mt-4 mb-3" id="title">
      {{ t("SHARED.CL_CONTRACT.TERMS_CONDITIONS_CONTRACT") }}
    </h1>
    <hr class="p-0 m-0" id="header-separator" />
  </div>
  <div class="position-relative">
    <InStoreOfferSummary
      v-if="fromInStoreFlow && userOffer"
      :amount="userOffer.currency + userOffer.approvedCreditLine"
      :annual-percentage-rate="Number(userOffer.annualPercentageRate)"
      :payment-term="String(userOffer.paymentTerm)"
      :hide-modal="!userOffer.example"
      @show-modal-interest-exp="$emit('showModalInterestExp')"
    />
    <img
      v-if="fromInStoreFlow"
      :src="useAsset('img/credit-line/coins.png')"
      alt="coins"
      style="width: 100vw !important; left: -5%"
      class="position-absolute top-0"
    />
  </div>

  <div class="position-relative">
    <button
      class="cta-t-c my-4 w-100 text-start"
      v-if="isPrivacyPolicyEnabled"
      @click="$emit('setDocModalContent', RegistrationSteps.PRIVACY_POLICY)"
    >
      {{ t("SHARED.SEE_PRIVACY_POLICY") }}
      <span class="ms-3 text-end float-end">&#10095;</span>
    </button>
    <hr class="p-0 mx-0 my-1" />
    <button
      class="cta-t-c my-4 w-100 text-start"
      @click="$emit('setDocModalContent', RegistrationSteps.ACCEPT)"
    >
      {{ t("SHARED.CL_CONTRACT.SEE_TYC") }}
      <span class="ms-3 text-end float-end">&#10095;</span>
    </button>
    <hr class="p-0 mx-0 my-1" />
    <button
      class="cta-t-c my-4 w-100 text-start"
      @click="() => $emit('setDocModalContent', RegistrationSteps.CONTRACT)"
    >
      {{ t("SHARED.CL_CONTRACT.SEE_CONTRACT") }}
      <span class="ms-3 text-end float-end">&#10095;</span>
    </button>
    <hr class="p-0 mx-0 my-1" />
    <div class="mt-3">
      <VueSignaturePad
        width="100%"
        height="125px"
        ref="signatureStepPadRef"
        class="signature-pad"
        :options="{ onEnd: () => (disableAcceptBtn = false) }"
      />
      <div class="d-flex justify-content-between">
        <h6
          class="text-right mt-2"
          @click="clearSignature"
          style="color: #2c7040"
        >
          {{ t("SHARED.CLEARSIGNATURE") }}
        </h6>
      </div>
    </div>
    <div class="form-check py-1 mt-3">
      <input
        type="checkbox"
        class="checkbox form-check-input"
        id="preference-checkbox"
        @change="(event: Event)=>$emit('onChangeMarketingCommunications', event)"
        :checked="marketingCommunicationsCheckbox"
        data-testid="checkbox"
      />
      <label
        for="preference-checkbox"
        class="form-check-label ms-2 my-auto fst-italic fw-light"
        >{{ t("SHARED.CL_CONTRACT_MARKETING_CHECKBOX") }}</label
      >
    </div>
    <p id="pgraf-terms" class="mt-2">
      {{ t("SHARED.CL_CONTRACT.DISCLAIMER") }}
    </p>
    <div>
      <button
        class="sign-btn"
        @click="signContract"
        :disabled="disableAcceptBtn"
        :class="disableAcceptBtn ? 'btn-disabled' : ''"
        id="sign-contract-btn"
      >
        {{ t("SHARED.SIGN_ACCEPT") }}
      </button>
      <Button
        class="reject-link mt-4 mb-0"
        @click="rejectOffer"
        id="reject-offer-btn"
      >
        {{ t("SHARED.NEW_CL_OFFER_ITEMS.NOT_INTERESTED") }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, Ref } from "vue";
import { useI18n } from "vue-i18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import type { IOffer } from "@/apps/credit-line/models/IOffer";
import { RegistrationSteps } from "@/apps/credit-line/types/registrationSteps";
import { VueSignaturePad } from "vue-signature-pad";
import InStoreOfferSummary from "@/apps/credit-line/components/InStore/InStoreOfferSummary.vue";
import Button from "@/apps/credit-line/components/Button.vue";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";

interface Props {
  userOffer?: IOffer;
  marketingCommunicationsCheckbox?: boolean;
  fromInStoreFlow?: boolean;
  fullName?: string;
  isPrivacyPolicyEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  marketingCommunicationsCheckbox: false,
  fromInStoreFlow: false,
});

const emit = defineEmits([
  "onEndDrawing",
  "signContract",
  "onChangeMarketingCommunications",
  "setDocModalContent",
  "disableAcceptBtn",
  "showModalInterestExp",
  "clearSignature",
  "switchStep",
]);

const { t } = useI18n();
const signatureStepPadRef = ref() as Ref<typeof VueSignaturePad>;
const disableAcceptBtn = ref<boolean>(true);
const showTitle = computed(() => !props.fromInStoreFlow);

const signContract = () => {
  emit(
    "signContract",
    signatureStepPadRef.value
      ? signatureStepPadRef.value.saveSignature()
      : null,
  );
};

const clearSignature = () => {
  signatureStepPadRef.value.clearSignature();
  disableAcceptBtn.value = true;
};

const rejectOffer = () => {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.ClickNotInterested,
    "Offer",
    "User declines offer",
  );
  emit("switchStep", RegistrationSteps.REJECT);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";

#pgraf-terms {
  color: #666;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 0.8125rem;
  font-style: normal;
  font-weight: 400;
}

.cta-t-c {
  font-feature-settings: "clig" off, "liga" off;
  border: none;
  background-color: transparent;
  font-weight: bolder;
  font-family: Inter, serif;
  font-size: 15px;
  font-style: normal;
  color: #2c7040;
}

.offer-bg {
  background-color: #f6fbf9 !important;
  height: 90vh;
}

.light-text {
  color: $pj_color-cool-gray;
}

.accept-offer-btn {
  background: $pj-color-green;
  border-radius: 11px;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.8px;
  color: $pj-color-white;
  width: 90%;
  height: 40px;
  border: none;
}

.accept-offer-btn-disabled {
  background-color: $pj-color-gray;
  cursor: not-allowed;
  border-radius: 11px;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.8px;
  color: $pj-color-white;
  width: 90%;
  height: 40px;
  border: none;
}

#modal-body-terms {
  max-height: 65vh !important;
}

#modal-body-contract {
  max-height: 65vh !important;
}

.check-dot {
  width: 25px;
  height: 25px;
  background: rgba(255, 242, 242, 0.49);
  border: 1px solid $pj-color-black;
  box-sizing: border-box;
  margin-left: 10%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.24px;
}

.check-legend {
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0.24px;
}

.payments-card {
  padding: 2.5%;
  border-radius: 10px;
  border-color: $pj-color-light-gray;
  border-width: thin;
  border-style: solid;
}

.option-btn {
  width: 100%;
  height: 50px;
  background-color: $pj-color-white;
  border-radius: 5px;
  color: $pj-color-green;
  text-align: left;
  font-size: 5vw;
  padding-left: 2.5%;
  border-color: $pj-color-green;
  border-width: thin;
}

.active-option {
  width: 100%;
  height: 50px;
  border: none;
  background-color: $pj-color-yellow-green;
  border-radius: 5px;
  color: $pj-color-white;
  text-align: left;
  font-size: 5vw;
  padding-left: 2.5%;
}

.signature-pad {
  border-radius: 25px;
  border-color: $pj-color-light-gray;
  border-width: thin;
  border-style: solid;
}

.sign-btn {
  background: $pj-color-green;
  border-radius: 11px;
  font-style: normal;
  font-weight: 600;
  left: 0;
  right: 0;
  width: 100%;
  margin: 1rem auto;
  bottom: 5.5%;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.8px;
  color: $pj-color-white;
  height: 40px;
  border: none;
}

.disabled {
  background-color: $pj-color-gray;
}

.offer-summary {
  display: flex;
  flex-direction: column;
  background: $pj-color-green-hover;
  padding: 10px;
  border-radius: 16px;
}

#other-offer-card {
  padding: 5%;
  background-color: $pj-color-green-hover;
  border-radius: 5%;
}

.loading-bg {
  background: #fff;
}

.gray-text {
  color: $pj-color-gray;
  font-weight: lighter;
}

.btn-disabled {
  background-color: $pj-color-gray;
  cursor: not-allowed;
}

.c1c {
  scale: 0.87 1 !important;
}

.sticky-container {
  position: sticky;
  bottom: 0;
  padding-bottom: 2rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-items: center;
  border-top: $pj-color-green 2px solid;
  background-color: $pj-color-white;
}

.cancel-btn {
  color: $pj-color-green;
  background-color: white;
  border-color: transparent;
}

.clear-btn {
  color: $pj-color-yellow-green;
}

#preference-checkbox {
  height: 1rem;
  width: 1rem;
}

label {
  cursor: pointer;
  color: $pj-color-green;
  font-weight: bold;
  font-size: medium;
}

.checkbox {
  box-shadow: none;
  outline: none;
  border-color: $pj-color-green;

  &:checked {
    background-color: $pj-color-green;
    color: $pj-color-green;
    outline: none;
    border: 0;
    box-shadow: none;
  }
}

#credit-explainer {
  background-color: $pj-color-nav-bar-gray;
  width: 100%;
  position: relative;
  padding-top: 1.13rem;
  padding-bottom: 0.81rem;

  & h5 {
    & span {
      color: $pj-color-green;
      font-family: Inter, serif;
      font-size: 2.25rem;
      font-style: normal;
      font-weight: 600;
      line-height: 2.5rem;
      letter-spacing: 0.01125rem;
    }
  }

  & .container {
    & .row {
      margin-top: 0.5rem;
    }
  }
}

#credit-explainer::before,
#credit-explainer::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  width: 10%;
  background-color: $pj-color-nav-bar-gray;
}

#credit-explainer::before {
  left: -2rem;
}

#credit-explainer::after {
  right: -2rem;
}

#offer-explainer-footer {
  border-radius: 0 0 1rem 1rem;
  background: #f8fbf5;
  position: absolute;
  left: 0;
  right: 0;
  width: 90%;

  & .container {
    & .row {
      margin-top: 0.5rem;
      color: #666;
      font-family: Inter, serif;
      font-size: 0.9375rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.4375rem;
      letter-spacing: 0.01875rem;
    }
  }
}

#offer-explainer-footer > div:nth-child(n) {
  margin-top: 0.5rem;
  color: #666;
}

.reject-link {
  color: $pj-color-green;
  text-align: center;
  font-feature-settings: "clig" off, "liga" off;
  font-family: Inter, serif;
  font-size: 0.875rem;
  font-style: normal;
  background-color: transparent;
  border: none;
  box-shadow: none;
  font-weight: 500;
  line-height: 144%; /* 1.26rem */
}

#title {
  color: black;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 500;
}

* {
  font-family: Inter, serif !important;
}

#header-separator {
  width: 100%;
  position: absolute;
  left: 0;
}
</style>
